import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import "./control-motion-recipes.css";

/**
 * ToggleSwitch — Tahoe geometry (approved 2026-06-10).
 *
 * Track: 44x26 rounded-pill
 *   OFF: --color-input-fill track, no stroke
 *   ON:  bg-accent + glow shadow, transparent border
 *
 * Knob: 22px white circle, 2px inset from track edges.
 *   Travel: track(44) - knob(22) - 2xinset(4) = 18px
 *   OFF translate: 0 (2px gap from left edge via padding)
 *   ON  translate: translateX(18px)
 *
 * Disabled: 40% opacity, pointer-events-none.
 */

type ToggleSwitchProps = Omit<React.ComponentProps<"button">, "onChange"> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const ToggleSwitch = forwardRef<HTMLButtonElement, ToggleSwitchProps>(
  ({ checked, onCheckedChange, disabled, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={disabled ? undefined : () => onCheckedChange(!checked)}
        className={cn(
          "denker-toggle-motion relative inline-flex h-[26px] w-[44px] shrink-0 cursor-pointer items-center rounded-pill transition-all duration-150",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          checked
            ? "border border-transparent bg-accent shadow-glow-accent-sm"
            : "border border-transparent bg-input-fill",
          className,
        )}
        {...props}
      >
        {/* Knob stays white to match the native macOS switch appearance. */}
        <span
          className={cn(
            "pointer-events-none block h-[22px] w-[22px] rounded-full bg-white shadow-btn-secondary",
            checked ? "translate-x-[18px]" : "translate-x-[2px]",
          )}
        />
      </button>
    );
  },
);
ToggleSwitch.displayName = "ToggleSwitch";

export { ToggleSwitch };
export type { ToggleSwitchProps };
